const MODAL_COOKIE = "product_modal_seen";

// Modal only shows at this width and above
const DESKTOP_QUERY = "(min-width: 768px)";

// Seconds the modal stays suppressed after being shown
const INTERVAL_SECONDS = {
  daily: 60 * 60 * 24,
};

function getCookie(name) {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function setCookie(name, value, maxAgeSeconds) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function isDesktop() {
  return window.matchMedia(DESKTOP_QUERY).matches;
}

class ProductModal extends HTMLElement {
  constructor() {
    super();
    this.startTimer = this.startTimer.bind(this);
    this.addToCart = this.addToCart.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  get interval() {
    return this.dataset.interval || "always";
  }

  // True if the customer has already seen the modal within the current interval.
  // Removes any cookie left over from a different interval setting.
  isSuppressed() {
    const stored = getCookie(MODAL_COOKIE);
    if (stored === null) return false;

    if (stored !== this.interval || !(this.interval in INTERVAL_SECONDS)) {
      deleteCookie(MODAL_COOKIE);
      return false;
    }

    return true;
  }

  rememberShown() {
    const maxAge = INTERVAL_SECONDS[this.interval];
    if (maxAge) setCookie(MODAL_COOKIE, this.interval, maxAge);
  }

  connectedCallback() {
    this.container = this.querySelector(".productModalContainer");
    this.form = this.querySelector(".productModalForm");
    this.errorMessage = this.querySelector(".productModalError");
    this.closeButton = this.querySelector(".productModalClose");
    this.background = this.querySelector(".productModalBackground");

    if (!isDesktop()) return;
    if (this.isSuppressed()) return;

    this.form?.addEventListener("submit", this.addToCart);
    this.closeButton?.addEventListener("click", this.close);
    this.background?.addEventListener("click", this.close);
    document.addEventListener("keydown", this.onKeydown);

    if (document.readyState === "complete") {
      this.startTimer();
    } else {
      window.addEventListener("load", this.startTimer, { once: true });
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    window.removeEventListener("load", this.startTimer);
    this.form?.removeEventListener("submit", this.addToCart);
    this.closeButton?.removeEventListener("click", this.close);
    this.background?.removeEventListener("click", this.close);
    document.removeEventListener("keydown", this.onKeydown);
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.container && !this.container.hidden) {
      this.close();
    }
  }

  startTimer() {
    if (this.closed || this.shown) return;

    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      // Viewport may have shrunk (e.g. tablet rotated) while waiting
      if (!isDesktop()) return;

      if (this.container && !this.closed) {
        this.container.hidden = false;
        this.shown = true;
        this.rememberShown();
      }
    }, 5000);
  }

  close() {
    clearTimeout(this.timer);
    this.closed = true;

    if (this.container) {
      this.container.hidden = true;
    }
  }

  async addToCart(event) {
    event.preventDefault();

    if (this.isAdding) return;

    const form = event.currentTarget;
    const button = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    const root = window.Shopify?.routes?.root || "/";

    if (!formData.has("quantity")) {
      formData.set("quantity", "1");
    }

    this.isAdding = true;

    const wasDisabled = button?.disabled ?? false;
    if (button) button.disabled = true;

    form.setAttribute("aria-busy", "true");

    if (this.errorMessage) {
      this.errorMessage.textContent = "";
      this.errorMessage.hidden = true;
    }

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.status >= 400) {
        throw new Error(
          result.description ||
            result.message ||
            "Unable to add this item to your cart.",
        );
      }

      window.location.assign(`${root}cart`);
    } catch (error) {
      if (this.errorMessage) {
        this.errorMessage.textContent =
          error.message || "Something went wrong. Please try again.";
        this.errorMessage.hidden = false;
      }
    } finally {
      this.isAdding = false;

      if (button) button.disabled = wasDisabled;
      form.removeAttribute("aria-busy");
    }
  }
}

if (!customElements.get("product-modal")) {
  customElements.define("product-modal", ProductModal);
}